import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { GlobalStyle } from './GlobalStyle';
import { AgriculturalInputs } from './pages/AgriculturalInputs';
import { CopasulCulture } from './pages/CopasulCulture';
import { Governance } from './pages/Governance';
import { GrainCommercial } from './pages/GrainCommercial';
import { Home } from './pages/Home';
import { Irrigation } from './pages/Irrigation';
import { News } from './pages/News';
import { Materia, MateriaAntiga } from './pages/News/Materia';
import { OurHistory } from './pages/OurHistory';
import { OurUnits } from './pages/OurUnits';
import { PhotoGallery } from './pages/PhotoGallery';
import { Album, AlbumAntigo } from './pages/PhotoGallery/Album';
import { ReportsDocuments } from './pages/ReportsDocuments';
import { SolarEnergy } from './pages/SolarEnergy';
import { SrSakae } from './pages/SrSakae';
import { Starch } from './pages/Starch';
import { Sustainability } from './pages/Sustainability';
import { TechnicalAssistence } from './pages/TechnicalAssistence';
import { TrrCopasul } from './pages/TrrCopasul';
import { Wiring } from './pages/Wiring';
import { VideoGallery } from './pages/VideoGallery';
import { RuralCopasul } from './pages/RuralCopasul';
import { Magazines } from './pages/Magazines';
import { Contact } from './pages/Contact';
import { WorkWithUs } from './pages/WorkWithUs';
import { PressRoom } from './pages/PressRoom';
import { Interviwes } from './pages/Interviwes';
import { PageInterview } from './pages/Interviwes/components/PageInterview';
import { EventCopasul } from './pages/EventCopasul';
import { BuildingSoils } from './pages/BuildingSoils';
import { useStartupState } from './Providers/StartupState';
import { NotFound } from './pages/NotFound';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TelaDeCarregamento, useFaseDoCarregamento, useTelaDeCarregamento } from './components/Loading';

// A área entre o cabeçalho e o rodapé. Enquanto a página carrega, tem altura
// fixa e esconde o que passar dela: a página por trás troca de estado (vazia,
// <Loading />, conteúdo) sem o rodapé pular -- e a tela de carregamento, por
// cima, nunca muda de tamanho. Na saída a altura é liberada.
//
// A altura é a da tela menos o cabeçalho e o rodapé, para o rodapé inteiro
// ficar à vista enquanto carrega. No celular o rodapé é mais alto que a tela e
// não cabe de jeito nenhum: aí o carregamento fica com 60% da tela (com 260px
// sobrava pouco para ele e o rodapé tomava a tela) e aparece o começo do rodapé.
const ALTURA_MINIMA = 260;
const alturaDoCarregamento = (main) => {
  const topo = main ? main.getBoundingClientRect().top + window.scrollY : 88;
  const rodape = document.querySelector('footer')?.offsetHeight || 380;
  const sobra = Math.round(window.innerHeight - topo - rodape);
  return sobra >= ALTURA_MINIMA ? sobra : Math.round(window.innerHeight * 0.6);
};

const ConteudoDoSite = ({ children }) => {
  const fase = useFaseDoCarregamento();
  const carregando = fase === 'carregando';
  const ref = useRef(null);
  const [altura, setAltura] = useState(null);

  useLayoutEffect(() => {
    if (!carregando) return undefined;
    const medir = () => setAltura(alturaDoCarregamento(ref.current));
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [carregando]);

  return (
    <main
      ref={ref}
      data-conteudo
      style={{
        position: 'relative',
        ...(carregando ? { height: altura ?? alturaDoCarregamento(null), overflow: 'hidden' } : {}),
      }}
    >
      {children}
      <TelaDeCarregamento />
    </main>
  );
};

export const App = () => {
  const { error } = useStartupState();
  useTelaDeCarregamento();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (window.location.href.toLowerCase().indexOf('localhost') === -1) {
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      let page = window.location.pathname;
      if (page.startsWith('/'))
        page = page.substring(1);

      if(page === '')
        page = 'home';

      gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: `/${page}`,
        page_title: page
      });
      console.log(page);
    }
  }, [location]);

  if (error) return navigate('*');
  return (
    <div className="App">
      <Header />
      <GlobalStyle />
      <ConteudoDoSite>
      <Routes>
        <Route path="/" element={<Home />} end />
        <Route path="nossaHistoria" element={<OurHistory />} />
        <Route path="comercialDeGraos/*" element={<GrainCommercial />} />
        <Route path="insumosAgricola" element={<AgriculturalInputs />} />
        <Route path="governanca" element={<Governance />} />
        <Route path="culturaCopasul" element={<CopasulCulture />} />
        <Route path="srSakae" element={<SrSakae />} />
        <Route path="fiacao" element={<Wiring />} />
        <Route path="fecularia" element={<Starch />} />
        <Route path="irrigacao" element={<Irrigation />} />
        <Route path="energiaSolar" element={<SolarEnergy />} />
        <Route path="trrCopasul" element={<TrrCopasul />} />
        <Route path="assistenciaTecnica" element={<TechnicalAssistence />} />
        <Route path="nossasUnidades" element={<OurUnits />} />
        <Route path="construindoSolos" element={<BuildingSoils />} />
        <Route path="sustentabilidade" element={<Sustainability />} />
        <Route path="relatoriosDocumentos" element={<ReportsDocuments />} />
        <Route path="noticias" element={<News />} />
        <Route path="noticias/:endereco" element={<Materia />} />
        {/* endereço do modelo antigo: redireciona para /noticias/<endereço> */}
        <Route path="paginaNoticia/:idNew" element={<MateriaAntiga />} />
        <Route path="galeriaDeFotos" element={<PhotoGallery />} />
        <Route path="galeriaDeFotos/:endereco" element={<Album />} />
        {/* endereço antigo dos álbuns: redireciona para o novo */}
        <Route path="fotos/:idPhoto/*" element={<AlbumAntigo />} />
        <Route path="galeriaDeVideos" element={<VideoGallery />} />
        <Route path="copasulRural" element={<RuralCopasul />} />
        <Route path="revistasCopasul" element={<Magazines />} />
        <Route path="salaDeImprensa" element={<PressRoom />} />
        <Route path="entrevistas" element={<Interviwes />} />
        <Route path="paginaEntrevista/:idEntrevista" element={<PageInterview />} />
        <Route path="eventosCopasul" element={<EventCopasul />} />
        <Route path="trabalheConosco" element={<WorkWithUs />} />
        <Route path="contato" element={<Contact />} />
        <Route path="politicaPrivacidade" element={<PrivacyPolicy />} />
        
        {/* Tirar Comentário quando App do Produtor for Lançado */}
        {/* Página desligada. Para religar: import ProducerApp from './pages/Producer'
            <Route path="appProdutor" element={<ProducerApp/>}/> */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </ConteudoDoSite>
      <Footer />
    </div>
  );
};
